import React, { useState, useEffect } from "react";
import { Form, FormItem } from "../Base/Form";
import { Button } from "../Base/Button";
import { Dialog } from "../Base/Dialog";
import { Toast } from "../Base/Toast";
import { Message } from "../Base/Message";
import { EnhancedInput } from "../Base/Input/EnhancedInput/EnhancedInput";
import { Icon } from "../Icon";
import {
  SetLoginPasswordDialogProps,
  SetLoginPasswordFormValues,
} from "./SetLoginPasswordDialog.types";
import { useT } from "../../i18n/hooks";
import { setLoginPassword } from "../../api/auth";
import "./SetLoginPasswordDialog.scss";

export const SetLoginPasswordDialog: React.FC<SetLoginPasswordDialogProps> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const t = useT();
  const [form] = Form.useForm<SetLoginPasswordFormValues>();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof SetLoginPasswordFormValues, string>>
  >({});

  useEffect(() => {
    if (visible) {
      form.resetFields();
      setLoading(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setFormErrors({});
    }
  }, [visible, form]);

  const handleSubmit = async (values: SetLoginPasswordFormValues) => {
    try {
      setLoading(true);
      setFormErrors({});
      await setLoginPassword({
        password: values.newPassword,
        passwordConfirm: values.newPasswordConfirm,
      });
      form.resetFields();
      Message.success(t("auth.setLoginPasswordSuccess"));
      onClose();
      onSuccess?.();
    } catch (error: any) {
      const message = error?.message || t("auth.setLoginPasswordFailed");
      Toast.show({
        type: "failed",
        title: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldsChange = () => {
    const errors = form.getFieldsError();
    const newErrors: Partial<Record<keyof SetLoginPasswordFormValues, string>> =
      {};
    errors.forEach(field => {
      const fieldName = field.name[0] as keyof SetLoginPasswordFormValues;
      if (field.errors.length > 0) {
        newErrors[fieldName] = field.errors[0];
      }
    });
    setFormErrors(newErrors);
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      className="set-login-password-dialog"
      showCloseButton={true}
      closeButtonPosition="bottom-center"
    >
      <div className="set-login-password-dialog__title">
        {t("auth.setLoginPasswordTitle")}
      </div>
      <div className="set-login-password-dialog__desc">
        <Icon name="checkShield" size="sm" />
        <span>{t("auth.setLoginPasswordDesc")}</span>
      </div>
      <div className="set-login-password-dialog__form-wrap">
        <Form
          form={form}
          onFinish={handleSubmit}
          onFieldsChange={handleFieldsChange}
          className="set-login-password-dialog__form"
        >
          <FormItem
            name="newPassword"
            rules={[
              { required: true, message: t("auth.pleaseInputNewPassword") },
              { min: 8, message: t("auth.passwordTooShort") },
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseInputNewPassword")}
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              prefixIcon={{ icon: "password", variant: "golden" }}
              suffixContent={{
                icon: showNewPassword ? "eye" : "eye-closed",
                onClick: () => setShowNewPassword(!showNewPassword),
              }}
              error={!!formErrors.newPassword}
            />
          </FormItem>

          <FormItem
            name="newPasswordConfirm"
            rules={[
              {
                required: true,
                message: t("auth.pleaseConfirmPasswordAgain"),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("auth.passwordsNotMatch")));
                },
              }),
            ]}
          >
            <EnhancedInput
              placeholder={t("auth.pleaseConfirmPasswordAgain")}
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              prefixIcon={{ icon: "password", variant: "golden" }}
              suffixContent={{
                icon: showConfirmPassword ? "eye" : "eye-closed",
                onClick: () => setShowConfirmPassword(!showConfirmPassword),
              }}
              error={!!formErrors.newPasswordConfirm}
            />
          </FormItem>

          <Button
            type="submit"
            size="large"
            variant="primary"
            loading={loading}
            className="set-login-password-dialog__submit"
            block
          >
            {t("auth.setLoginPasswordSubmit")}
          </Button>

          <div className="set-login-password-dialog__footer">
            {t("auth.setLoginPasswordFooter")}
          </div>
        </Form>
      </div>
    </Dialog>
  );
};
